import { z } from 'zod';

export type EstimateStatus = 'draft' | 'sent' | 'viewed' | 'accepted' | 'declined' | 'expired' | 'cancelled';

/** Statuses the office can still withdraw (cancel). Accepted estimates carry a signature and stay locked. */
export const CANCELLABLE_ESTIMATE_STATUSES: EstimateStatus[] = ['draft', 'sent', 'viewed'];
/**
 * Deleting an accepted estimate destroys a signed customer record, so only
 * users who can see company financials may do it. Every other estimate can be
 * deleted by any staff member who can reach it — mistakes must be fixable.
 */
export function estimateDeleteIsRisky(status: EstimateStatus | string): boolean {
  return status === 'accepted';
}

export function canDeleteEstimate(status: EstimateStatus | string, canSeeFinancials: boolean): boolean {
  return canSeeFinancials || !estimateDeleteIsRisky(status);
}

export interface EstimateLineItem {
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
}

export interface Estimate {
  id: string;
  company_id: string;
  estimate_number: string;
  lead_id: string;
  customer_id: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  /** Optional on drafts (quotes can be printed); required to send online */
  sent_to_email: string | null;
  customer_address: string | null;
  service_description: string | null;
  line_items: EstimateLineItem[];
  subtotal: number;
  gst_amount: number;
  estimate_amount: number;
  valid_until: string | null;
  status: EstimateStatus;
  sent_at: string | null;
  accepted_at: string | null;
  declined_at: string | null;
  cancelled_at?: string | null;
  decline_reason: string | null;
  /** customer = via the public link; staff = marked in the office */
  declined_by: 'customer' | 'staff' | null;
  decliner_name: string | null;
  notes: string | null;
  terms: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // Tax config snapshot
  tax_rate_snapshot: number | null;
  tax_label_snapshot: string | null;
  tax_inclusive_snapshot: boolean | null;
  // Signature fields
  signature_token: string | null;
  signature_token_expires_at: string | null;
  signature_image_url: string | null;
  signed_at: string | null;
  signer_ip_address: string | null;
  signer_user_agent: string | null;
  signer_name: string | null;
  signature_consent: boolean;
  accepted_terms_url: string | null;
  accepted_privacy_url: string | null;
  // Deposit on acceptance (migration 20260924170000)
  /** % of the total asked when accepted; null/0 = none. Editable until accepted. */
  deposit_percent?: number | null;
  /** Owed, fixed at acceptance. */
  deposit_amount?: number | null;
  deposit_paid_amount?: number;
  deposit_paid_at?: string | null;
  deposit_payment_url?: string | null;
  deposit_provider_status?: string | null;
  deposit_job_id?: string | null;
}

export interface EstimateWithRelations extends Estimate {
  lead?: { lead_number: string; status?: string; customer_name?: string } | null;
  customer?: { customer_name: string; customer_number?: string | null; company_name?: string | null } | null;
  creator?: { full_name: string } | null;
}

// Zod schemas for validation
export const estimateLineItemSchema = z.object({
  description: z.string().min(1, 'Description required').max(500),
  quantity: z.number().min(0.01, 'Quantity must be positive'),
  unit_price: z.number().min(0, 'Price cannot be negative'),
  amount: z.number(),
});

export const createEstimateSchema = z.object({
  lead_id: z.string().uuid(),
  customer_id: z.string().uuid().optional().nullable(),
  customer_name: z.string().min(1, 'Customer name required').max(100),
  customer_phone: z.string().max(20).optional().nullable(),
  sent_to_email: z.string().trim().email('Valid email required').optional().or(z.literal('')),
  customer_address: z.string().max(500).optional().nullable(),
  service_description: z.string().max(2000).optional().nullable(),
  line_items: z.array(estimateLineItemSchema).min(1, 'At least one line item required'),
  valid_until: z.string().optional().nullable(),
  notes: z.string().max(2000).optional().nullable(),
  terms: z.string().max(2000).optional().nullable(),
});

export type CreateEstimateInput = z.infer<typeof createEstimateSchema>;

// Helper to calculate totals from line items
import { DEFAULT_TAX_CONFIG, taxFromNet, type TaxConfig } from '@/utils/tax';

export function calculateEstimateTotals(
  lineItems: EstimateLineItem[],
  taxConfig: TaxConfig = DEFAULT_TAX_CONFIG,
) {
  const subtotal = lineItems.reduce((sum, item) => sum + item.amount, 0);
  const gstAmount = taxFromNet(subtotal, taxConfig);
  const estimateAmount = subtotal + gstAmount;
  return { subtotal, gstAmount, estimateAmount };
}
